import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { getApiErrorMessage } from '../utils/errorMessage';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './Layout/LanguageSelector';
import { persistRedirectFromUrl, getRedirectUrl, getReturnPath } from '../utils/authRedirect';
import './Auth.css';

const Register = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();

  // Persist redirect/returnPath from URL to sessionStorage and show clean URL in address bar
  useEffect(() => {
    persistRedirectFromUrl(searchParams, '/register');
  }, [searchParams]);

  const redirectUrl = getRedirectUrl(searchParams);
  const returnPath = getReturnPath(searchParams);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '', 
    address: '',
    city: '',
    zipCode: '',
    role: 'client',
    clientType: 'private',
    companyName: '',
    vatNumber: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate required fields for company clients
    if (formData.role === 'client' && formData.clientType === 'company') {
      if (!formData.companyName || !formData.vatNumber) {
        setError(t('auth.companyRequired', 'Company name and VAT number are required for company clients.'));
        setLoading(false);
        return;
      }
    }

    try {
      // Prepare registration data
      const registrationData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        phone: formData.phone.trim(),
        address: formData.address.trim(),
        city: formData.city.trim() || undefined,
        zipCode: formData.zipCode.trim() || undefined,
        role: formData.role,
        ...(formData.role === 'client' && {
          clientType: formData.clientType,
          ...(formData.clientType === 'company' && {
            companyName: formData.companyName.trim(),
            vatNumber: formData.vatNumber.trim()
          })
        })
      };

      const response = await api.post('/auth/register', registrationData);

      if (response.data.success) {
        setRegistrationComplete(true);
      } else {
        setError(response.data.message || t('auth.registerFailed', 'Registration failed. Please try again.'));
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = getApiErrorMessage(error, t('auth.registerFailed', 'Registration failed. Please try again.'));
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-header-row">
        <LanguageSelector />
      </div>
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo-container">
            <img src="/images/logo-Photoroom.png" alt="Cleaning Service Logo" className="auth-logo" />
          </div>
          <h2>{t('auth.register', 'Register')}</h2>

          {registrationComplete ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>✉️</div>
              <h3 style={{ color: '#16a34a', marginBottom: '12px' }}>
                {t('auth.registrationSuccessTitle', 'Registration Successful!')}
              </h3>
              <p style={{ color: '#555', lineHeight: '1.6', marginBottom: '8px' }}>
                {t('auth.verifyEmailSent', 'We have sent a verification email to your inbox. Please check your email and click the verification link before logging in.')}
              </p>
              <p style={{ fontWeight: '600', color: '#333', marginBottom: '24px' }}>
                {formData.email}
              </p>
              <Link
                to="/login"
                className="btn btn-primary btn-block"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none', padding: '12px' }}
              >
                {t('auth.goToLogin', 'Go to Login')}
              </Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>{t('auth.fullName', 'Full Name')}</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('auth.email', 'Email')}</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('auth.password', 'Password')}</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="6"
              />
            </div>

            <div className="form-group">
              <label>{t('auth.phone', 'Phone')}</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('auth.address', 'Address')}</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="form-group">
                <label>{t('auth.city', 'City')}</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>{t('auth.zipCode', 'Zip Code')}</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('auth.registerAs', 'Register as')}</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="client">{t('auth.client', 'Client')}</option>
                <option value="cleaner">{t('auth.cleaner', 'Cleaner')}</option>
              </select>
            </div>

            {formData.role === 'client' && (
              <>
                <div className="form-group">
                  <label>{t('auth.clientType', 'Client Type')}</label>
                  <select
                    name="clientType"
                    value={formData.clientType}
                    onChange={handleChange}
                    required
                  >
                    <option value="private">{t('auth.private', 'Private')}</option>
                    <option value="company">{t('auth.company', 'Company')}</option>
                  </select>
                </div>

                {formData.clientType === 'company' && (
                  <>
                    <div className="form-group">
                      <label>{t('auth.companyName', 'Company Name')}</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required={formData.clientType === 'company'}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('auth.vatNumber', 'VAT Number')}</label>
                      <input
                        type="text"
                        name="vatNumber"
                        value={formData.vatNumber}
                        onChange={handleChange}
                        required={formData.clientType === 'company'}
                      />
                    </div>
                  </>
                )}
              </>
            )}

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button 
              type="submit" 
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? t('auth.registering', 'Registering...') : t('auth.register', 'Register')}
            </button>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <p style={{ color: '#666', fontSize: '14px' }}>
                {t('auth.alreadyHaveAccount', 'Already have an account?')}{' '}
                <Link
                  to="/login"
                  style={{ color: '#2c7a7b', textDecoration: 'none', fontWeight: '600' }}
                >
                  {t('auth.loginHere', 'Login here')}
                </Link>
              </p>
            </div>
          </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;

